import { useEffect, useState } from 'preact/hooks';
import { PAGELOOP_ENDPOINT, PAGELOOP_PROJECT } from '../data/pageloop-config';

/**
 * Best-effort detection of whether the visitor is already signed in to
 * PageLoop — so the marketing nav can show "Dashboard" instead of
 * "Sign in".
 *
 * The marketing site has no user system of its own. But it's served from
 * the SAME origin as the PageLoop admin SPA (`/admin`) and the API
 * (`/api/v1`) on pageloop.dev, and it embeds the comment widget pointed
 * at that endpoint. That gives us two same-origin signals:
 *
 *   - `pl_widget_session` — the bearer the embedded widget persists when
 *     a visitor signs in through it.
 *   - `pl_admin_session`  — the bearer the admin dashboard persists.
 *
 * We read whichever exists and verify it against the endpoint's `me`
 * RPC (a stale/expired token returns null, so we never show a false
 * "Dashboard"). It's deliberately hackish: cross-origin (e.g. local dev
 * on localhost) simply yields `null` and the nav falls back to
 * "Sign in".
 *
 * Re-checks on tab focus, cross-tab storage writes, and a custom
 * `pageloop:auth-maybe-changed` window event (dispatched by the widget
 * shell on `auth:changed`) so a sign-in/out through the on-page widget
 * updates the nav without a reload.
 */

/** localStorage keys the two PageLoop surfaces persist their bearer under. */
const TOKEN_KEYS = ['pl_widget_session', 'pl_admin_session'] as const;

export interface WidgetUser {
	/** Display name (falls back to email) — used for the nav label/title. */
	name: string;
	email: string;
}

function readToken(): string | null {
	if (typeof localStorage === 'undefined') return null;
	for (const key of TOKEN_KEYS) {
		const v = localStorage.getItem(key);
		if (v) return v;
	}
	return null;
}

export function useWidgetSession(): WidgetUser | null {
	const [user, setUser] = useState<WidgetUser | null>(null);

	useEffect(() => {
		let cancelled = false;
		let inFlight = false;

		async function check(): Promise<void> {
			if (inFlight) return;
			const token = readToken();
			if (!token) {
				if (!cancelled) setUser(null);
				return;
			}
			inFlight = true;
			try {
				const res = await fetch(`${PAGELOOP_ENDPOINT}/api/v1/rpc`, {
					method: 'POST',
					headers: {
						'content-type': 'application/json',
						'x-pageloop-project': PAGELOOP_PROJECT,
						authorization: `Bearer ${token}`,
					},
					body: JSON.stringify({ method: 'me', params: {}, projectId: PAGELOOP_PROJECT }),
				});
				const json = (await res.json().catch(() => null)) as
					| { result?: { displayName?: string | null; email?: string } | null; error?: unknown }
					| null;
				const me = res.ok && json && !json.error ? (json.result ?? null) : null;
				if (cancelled) return;
				setUser(
					me ? { name: me.displayName || me.email || 'Account', email: me.email ?? '' } : null,
				);
			} catch {
				// Network / CORS (e.g. local dev) → treat as logged out.
				if (!cancelled) setUser(null);
			} finally {
				inFlight = false;
			}
		}

		void check();

		const onVisible = () => {
			if (document.visibilityState === 'visible') void check();
		};
		const onStorage = (e: StorageEvent) => {
			if (!e.key || (TOKEN_KEYS as readonly string[]).includes(e.key)) void check();
		};
		const onAuth = () => void check();

		document.addEventListener('visibilitychange', onVisible);
		window.addEventListener('storage', onStorage);
		window.addEventListener('pageloop:auth-maybe-changed', onAuth);
		return () => {
			cancelled = true;
			document.removeEventListener('visibilitychange', onVisible);
			window.removeEventListener('storage', onStorage);
			window.removeEventListener('pageloop:auth-maybe-changed', onAuth);
		};
	}, []);

	return user;
}
